import React, { useEffect, useState } from "react";
import { X, Info } from "lucide-react";

// Shared look for the allowance / deduction modals (single-row "+" / "−"
// buttons and the bulk BulkAdjustmentModal): gradient header, flat inputs,
// accent-coloured Gross/Net toggle and buttons. Styling only — no behaviour.

export const ADJUSTMENT_ACCENTS = {
  allowance: { accent: "#10b981", accentDark: "#059669" },
  deduction: { accent: "#ef4444", accentDark: "#dc2626" },
};

// Class for the scrolling modal box — hides its scrollbar (still scrollable
// by wheel/touch/keyboard). The ::-webkit rule lives in the <style> the
// header renders, since inline styles can't target pseudo-elements.
export const ADJUSTMENT_SCROLL_CLASS = "adjustment-modal-scroll";

export const getAdjustmentFormStyles = ({ theme, gridCols = 3 }) => {
  const isDark = theme === "dark";
  const text = isDark ? "#f1f5f9" : "#0f172a";
  const card = {
    position: "relative",
    overflow: "hidden",
    background: isDark ? "#1e293b" : "#ffffff",
    borderRadius: "12px",
    padding: "16px 18px",
    paddingInlineStart: "22px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
    marginBottom: "12px",
    transition: "all 0.2s ease",
  };
  return {
    modal: {
      position: "fixed",
      inset: 0,
      zIndex: 3000,
      background: "rgba(10, 15, 29, 0.72)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
    },
    modalContent: {
      width: "100%",
      maxWidth: "760px",
      maxHeight: "calc(100vh - 32px)",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      background: isDark ? "#0f172a" : "#ffffff",
      borderRadius: "18px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)",
      animation: "fadeInUp 0.2s ease-out",
    },
    body: { padding: "22px" },
    section: {
      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
      borderRadius: "14px",
      padding: "18px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
      marginBottom: "18px",
    },
    sectionTitle: { margin: 0, color: text, fontSize: "16px", fontWeight: 700 },
    allowanceCard: card,
    deductionCard: card,
    formGrid: {
      display: "grid",
      gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
      gap: "14px",
      marginBottom: "16px",
    },
    formGroup: { display: "flex", flexDirection: "column", gap: "8px" },
    label: { color: text, fontSize: "13px", fontWeight: 600 },
    input: {
      width: "100%",
      boxSizing: "border-box",
      background: isDark ? "#1e293b" : "#ffffff",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
      borderRadius: "10px",
      padding: "11px 14px",
      color: text,
      fontSize: "14px",
      fontWeight: 500,
      outline: "none",
      transition: "border-color 0.15s ease",
    },
  };
};

export const AdjustmentModalHeader = ({ icon: Icon, title, subtitle, accent, accentDark, onClose, isRTL }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        padding: "18px 22px",
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
      }}
    >
      <style>{`.${ADJUSTMENT_SCROLL_CLASS}::-webkit-scrollbar { display: none; }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={22} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>{title}</h3>
          {subtitle && (
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "13px",
                opacity: 0.92,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={isRTL ? "إغلاق" : "Close"}
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
};

// Two-option pill toggle (Net / Gross). `options`: [{ label, active, onClick }]
export const SegmentedToggle = ({ options, accent, theme }) => {
  const isDark = theme === "dark";
  return (
    <div
      style={{
        display: "flex",
        gap: "4px",
        padding: "4px",
        borderRadius: "10px",
        background: isDark ? "rgba(255,255,255,0.05)" : "#eef2f7",
      }}
    >
      {options.map((o) => (
        <button
          key={o.label}
          type="button"
          onClick={o.onClick}
          style={{
            flex: 1,
            padding: "9px 10px",
            borderRadius: "8px",
            border: "none",
            background: o.active ? accent : "transparent",
            color: o.active ? "#ffffff" : isDark ? "#94a3b8" : "#64748b",
            fontSize: "13px",
            fontWeight: 700,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

export const HelpNote = ({ children, theme }) => (
  <p
    style={{
      display: "flex",
      gap: "8px",
      alignItems: "flex-start",
      margin: "8px 2px 0",
      fontSize: "12.5px",
      lineHeight: 1.65,
      color: theme === "dark" ? "#94a3b8" : "#64748b",
    }}
  >
    <Info size={14} style={{ flexShrink: 0, marginTop: "3px" }} />
    <span>{children}</span>
  </p>
);

// variant "solid" = gradient primary action; "ghost" = outlined secondary.
export const AccentButton = ({ icon: Icon, children, onClick, disabled, accent, accentDark, variant = "solid", theme }) => {
  const [hover, setHover] = useState(false);
  const isDark = theme === "dark";
  const solid = variant === "solid";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: solid ? "10px 20px" : "8px 14px",
        borderRadius: "10px",
        border: solid ? "none" : `1px solid ${hover ? `${accent}80` : isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
        background: solid
          ? `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`
          : hover
            ? `${accent}1a`
            : "transparent",
        color: solid ? "#ffffff" : hover ? accent : isDark ? "#cbd5e1" : "#475569",
        fontSize: "13.5px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        boxShadow: solid ? `0 4px 12px ${accent}4d` : "none",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
      }}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};
